// src/components/ChatInterface.js
import React, { useState, useRef, useEffect, useCallback } from "react";

import { chatAiApi } from "../../services/ChatAiApi";
import { promptsApi } from "../../services/PromptsApi";
import webSocketService from "../../services/WebSocketService";
import GenAIOptions from "./GenAIOptions";
import DataSourceFilter from "./DataSourceFilter";
import ModeSelector from "./ModeSelector";
import AgentReasoningDisplay from "./AgentReasoningDisplay";
import MarkdownRenderer from "./MarkdownRenderer";
import EnhancedCitationDisplay from "./EnhancedCitationDisplay";
import { requestQueueManager } from "../../services/apiConfig";

import {
  Header,
  SpaceBetween,
  TextContent,
  Button,
  Textarea,
  Box,
  Grid,
  Select,
  FormField,
  Icon,
  Badge,
  Modal,
  SegmentedControl,
} from "@cloudscape-design/components";
import "./ChatInterface.css";

const CustomButton = ({ onClick, iconName, children }) => (
  <Button
    onClick={onClick}
    iconName={iconName}
    variant="primary"
    className="custom-button"
  >
    {children}
  </Button>
);

const Citation = ({ citation }) => {
  const getSourceColor = (sourceType) => {
    switch (sourceType) {
      case "document":
        return "blue";
      case "pdf":
        return "red";
      case "text":
        return "green";
      case "s3":
        return "purple";
      case "kendra":
        return "orange";
      case "web":
        return "green";
      default:
        return "grey";
    }
  };

  const getSourceTypeLabel = (sourceType) => {
    switch (sourceType) {
      case "document":
        return "Document";
      case "pdf":
        return "PDF";
      case "text":
        return "Text";
      case "s3":
        return "S3 Bucket";
      case "kendra":
        return "Kendra";
      case "web":
        return "Web";
      default:
        return "Source";
    }
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <Box padding="s" variant="awsui-key-label" className="citation">
      <TextContent>
        <div style={{ fontSize: "90%", color: "#545b64" }}>
          <SpaceBetween direction="horizontal" size="xs">
            <strong>Source:</strong>
            <Badge color={getSourceColor(citation.sourceType || "unknown")}>
              {getSourceTypeLabel(citation.sourceType || "unknown")}
            </Badge>
            {citation.score && (
              <Badge color="blue">
                Relevance: {Math.round(citation.score * 100)}%
              </Badge>
            )}
          </SpaceBetween>

          {/* Document Title/Source Name */}
          <div style={{ margin: "6px 0" }}>
            {citation.uri && isValidUrl(citation.uri) ? (
              <a
                href={citation.uri}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#0073bb",
                  textDecoration: "none",
                  fontWeight: "500",
                  fontSize: "95%",
                }}
                onMouseOver={(e) =>
                  (e.target.style.textDecoration = "underline")
                }
                onMouseOut={(e) => (e.target.style.textDecoration = "none")}
              >
                {citation.documentTitle || citation.source}
                <Icon
                  name="external"
                  size="small"
                  style={{ marginLeft: "4px" }}
                />
              </a>
            ) : (
              <span
                style={{ color: "#16191f", fontWeight: "500", fontSize: "95%" }}
              >
                {citation.documentTitle || citation.source}
              </span>
            )}
          </div>

          {/* Content Preview */}
          {citation.content && (
            <p
              style={{
                margin: "6px 0",
                color: "#687078",
                fontSize: "85%",
                fontStyle: "italic",
                lineHeight: "1.3",
              }}
            >
              "{citation.content}"
            </p>
          )}

          {/* URI/Link (if not already shown as clickable link above) */}
          {citation.uri && !isValidUrl(citation.uri) && (
            <p
              style={{
                margin: "4px 0",
                color: "#879596",
                fontSize: "80%",
                wordBreak: "break-all",
                fontFamily: "monospace",
              }}
            >
              {citation.uri}
            </p>
          )}

          {/* Metadata information */}
          {citation.metadata && Object.keys(citation.metadata).length > 0 && (
            <details
              style={{ marginTop: "6px", fontSize: "80%", color: "#879596" }}
            >
              <summary style={{ cursor: "pointer", color: "#0073bb" }}>
                Additional metadata
              </summary>
              <pre
                style={{
                  marginTop: "4px",
                  fontSize: "75%",
                  background: "#f8f9fa",
                  padding: "4px",
                  borderRadius: "2px",
                  overflow: "auto",
                }}
              >
                {JSON.stringify(citation.metadata, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </TextContent>
    </Box>
  );
};

const MessageBubble = ({ message, onFeedbackClick }) => (
  <div className="message-bubble">
    <div className={`message-container ${message.role}`}>
      <div className="message-avatar">
        <Icon
          name={message.role === "user" ? "user-profile" : "settings"}
          size="medium"
          variant={message.role === "user" ? "normal" : "subtle"}
        />
      </div>
      <div className="message-content">
        {/* Show reasoning steps only while response is being generated */}
        {message.role === "assistant" &&
          message.reasoning &&
          message.reasoning.length > 0 &&
          (message.isStreaming ||
            !message.content ||
            message.content === "") && (
            <Box className="reasoning-container" padding={{ bottom: "s" }}>
              <AgentReasoningDisplay
                reasoningSteps={message.reasoning.map((step, index) => ({
                  id: `${message.id}-step-${index}`,
                  title: step.title || `Reasoning Step ${index + 1}`,
                  description: step.text || step.description || step,
                  status: step.isActive
                    ? "active"
                    : step.isCompleted
                    ? "completed"
                    : "pending",
                  details: step.details || {},
                  stepNumber: index + 1,
                }))}
                isActive={message.reasoning.some((step) => step.isActive)}
                currentStep={message.reasoning.findIndex(
                  (step) => step.isActive
                )}
                title="Agent Reasoning Process"
              />
            </Box>
          )}

        <div className="message-text">
          <MarkdownRenderer
            content={message.content}
            isStreaming={message.isStreaming}
          />
        </div>

        {message.citations && message.citations.length > 0 && (
          <Box className="citations-container">
            <EnhancedCitationDisplay 
              citations={message.citations}
              messageId={message.id}
              isAgentComplete={!message.isStreaming} // Only show when agent is complete
              onCitationClick={(citation) => {
                console.log('Citation clicked:', citation);
                // Handle citation click if needed
              }}
            />
          </Box>
        )}
        <div className="message-footer">
          <div className="message-timestamp">{message.timestamp}</div>
          {message.role === "assistant" && (
            <div className="feedback-section">
              {message.feedback ? (
                <Box className="feedback-display">
                  <Icon name="status-positive" />
                  <span>Rating: {message.feedback.rating}/5</span>
                </Box>
              ) : (
                <Button
                  variant="link"
                  onClick={() => onFeedbackClick(message)}
                  iconName="thumbs-up-down"
                >
                  Provide Feedback
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

const FeedbackModal = ({ visible, message, onSubmit, onDismiss }) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");

  // Reset form when modal is opened/closed
  useEffect(() => {
    if (visible) {
      setRating(null);
      setComment("");
    }
  }, [visible]);

  const handleSubmit = () => {
    console.log("FeedbackModal - Preparing submission:", {
      messageId: message?.id,
      rating,
      comment,
    });
    console.log("FeedbackModal - Full message object:", message);

    if (!rating) {
      console.log("No rating selected");
      return;
    }

    onSubmit({
      messageId: message?.id,
      rating: parseInt(rating),
      comment,
    });
  };

  const ratingOptions = [
    { id: "1", text: "1", description: "Not helpful" },
    { id: "2", text: "2", description: "Somewhat helpful" },
    { id: "3", text: "3", description: "Helpful" },
    { id: "4", text: "4", description: "Very helpful" },
    { id: "5", text: "5", description: "Extremely helpful" },
  ];

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Provide Feedback"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={!rating}>
              Submit Feedback
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <FormField
          label="Rate the response"
          description="Please select a rating from 1 to 5"
          errorText={!rating && "Rating is required"}
        >
          <SegmentedControl
            selectedId={rating}
            onChange={({ detail }) => setRating(detail.selectedId)}
            label="Rating"
            options={ratingOptions}
          />
        </FormField>
        <FormField label="Additional comments (optional)">
          <Textarea
            value={comment}
            onChange={({ detail }) => setComment(detail.value)}
            placeholder="What did you think about this response?"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [usage, setUsage] = useState(null); // eslint-disable-line no-unused-vars
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [genAIOptions, setGenAIOptions] = useState({});
  const [showGenAIOptions, setShowGenAIOptions] = useState(false);
  const [showDataSourceFilter, setShowDataSourceFilter] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]);
  const messagesEndRef = useRef(null);
  const [dataSourceError, setDataSourceError] = useState(null);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [progressMessage, setProgressMessage] = useState("");
  const [currentReasoning, setCurrentReasoning] = useState([]); // eslint-disable-line no-unused-vars
  const [queueStatus, setQueueStatus] = useState({
    chatQueue: { active: 0, queued: 0 },
    apiQueue: { active: 0, queued: 0 },
  });
  // Session management state
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("chatSessionId") || null;
  });
  const [userId, setUserId] = useState(() => {
    return localStorage.getItem("chatUserId") || null;
  });
  const [conversationTurn, setConversationTurn] = useState(1);

  // Mode selection and agent state
  const [selectedMode, setSelectedMode] = useState(() => {
    return localStorage.getItem("chatSelectedMode") || "bedrock-agent"; // Default to agent mode
  });
  const [agentStatus, setAgentStatus] = useState(null);
  const [wsConnectionStatus, setWsConnectionStatus] = useState({
    isConnected: false,
    isConnecting: false,
    connectionId: null,
  });

  const handleFeedbackClick = (message) => {
    console.log("Opening feedback modal for message:", message);
    console.log("Message ID being used for feedback:", message.id);
    setSelectedMessage(message);
    setFeedbackModalVisible(true);
  };

  const handleFeedbackSubmit = async (feedback) => {
    console.log("🔄 Submitting feedback:", feedback);
    try {
      // Find the message to get the API message ID
      const message = messages.find((msg) => msg.id === feedback.messageId);
      console.log("📋 Found message for feedback:", {
        messageId: message?.id,
        apiMessageId: message?.apiMessageId,
        hasContent: !!message?.content,
        mode: selectedMode,
      });

      const apiMessageId = message?.apiMessageId || feedback.messageId;
      console.log("🆔 Using API message ID for feedback:", apiMessageId);

      const responseData = await chatAiApi.submitFeedback({
        messageId: apiMessageId,
        rating: parseInt(feedback.rating),
        comment: feedback.comment || "",
      });

      console.log("✅ Feedback submission response data:", responseData);

      // Update the message in the UI to show feedback was provided
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === feedback.messageId
            ? { ...msg, feedback: responseData.feedback }
            : msg
        )
      );

      // Close the modal and reset selection
      setFeedbackModalVisible(false);
      setSelectedMessage(null);
    } catch (error) {
      console.error("❌ Error submitting feedback:", error);
      console.error("📊 Feedback context:", {
        feedbackMessageId: feedback.messageId,
        selectedMode,
        messageCount: messages.length,
        lastMessage: messages[messages.length - 1]?.id,
      });
      alert(`Failed to submit feedback: ${error.message}`);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Mode and agent management functions
  const handleModeChange = (newMode) => {
    console.log("Mode changed from", selectedMode, "to:", newMode);

    // Preserve session continuity across mode switches
    const previousMode = selectedMode;
    setSelectedMode(newMode);

    // Clear agent-specific state when switching to manual mode
    if (newMode === "manual") {
      // Agent-specific state cleared when switching modes
    }

    // If switching from manual to agent, check agent status
    if (newMode === "bedrock-agent" && previousMode === "manual") {
      checkAgentStatus();
    }

    // Log mode change for session tracking
    console.log("Session preserved during mode switch:", {
      sessionId,
      userId,
      conversationTurn,
      messageCount: messages.length,
    });
  };

  const checkAgentStatus = useCallback(async () => {
    // Use a ref to check current status without causing dependency issues
    setAgentStatus((prev) => {
      // Prevent multiple simultaneous agent status checks
      if (prev && prev.checking) {
        console.log("Agent status check already in progress, skipping...");
        return prev;
      }
      return { ...prev, checking: true };
    });

    try {
      const status = await chatAiApi.getAgentStatus();
      setAgentStatus({
        ...status,
        checking: false,
        lastChecked: Date.now(),
      });

      // If in agent mode, also try to reconnect WebSocket if needed
      if (selectedMode === "bedrock-agent" && status.available) {
        const wsStatus = webSocketService.getStatus();
        if (!wsStatus.isConnected && !wsStatus.isConnecting) {
          console.log(
            "Agent available but WebSocket disconnected, attempting to reconnect..."
          );
          setWsConnectionStatus((prev) => ({ ...prev, isConnecting: true }));

          try {
            await webSocketService.connect();
            console.log("WebSocket reconnected successfully");
          } catch (wsError) {
            console.error("Failed to reconnect WebSocket:", wsError);
            setWsConnectionStatus({
              isConnected: false,
              isConnecting: false,
              connectionId: null,
            });
          }
        }
      }

      // If agent becomes unavailable, switch to manual mode
      if (!status.available && selectedMode === "bedrock-agent") {
        console.warn("Agent unavailable, switching to manual mode");
        setSelectedMode("manual");
      }

      return status;
    } catch (error) {
      console.error("Error checking agent status:", error);
      const errorStatus = {
        available: false,
        error: error.message,
        checking: false,
        lastChecked: Date.now(),
      };
      setAgentStatus(errorStatus);

      // Switch to manual mode if agent check fails
      if (selectedMode === "bedrock-agent") {
        setSelectedMode("manual");
      }

      return errorStatus;
    }
  }, [selectedMode]);

  const fetchPrompts = useCallback(async () => {
    try {
      const data = await promptsApi.getPrompts();
      setPrompts(
        data.map((prompt) => ({
          label: prompt.title,
          value: prompt.id,
          context: prompt.promptContext,
        }))
      );
    } catch (error) {
      console.error("Error fetching prompts:", error);
      // Fallback to mock prompts if API fails
      const mockPrompts = [
        {
          id: "prompt-1",
          title: "Cloud Cost Optimization",
          promptContext:
            "Analyze cloud costs and provide optimization recommendations for AWS services including EC2, S3, and RDS.",
        },
        {
          id: "prompt-2",
          title: "Security Best Practices",
          promptContext:
            "Review security configurations and recommend best practices for cloud infrastructure security.",
        },
        {
          id: "prompt-3",
          title: "Performance Analysis",
          promptContext:
            "Analyze application performance metrics and suggest improvements for better scalability.",
        },
        {
          id: "prompt-4",
          title: "Architecture Review",
          promptContext:
            "Review current cloud architecture and provide recommendations for better design patterns.",
        },
        {
          id: "prompt-5",
          title: "Compliance Check",
          promptContext:
            "Evaluate compliance with industry standards like SOC2, HIPAA, and PCI-DSS.",
        },
      ];
      setPrompts(
        mockPrompts.map((prompt) => ({
          label: prompt.title,
          value: prompt.id,
          context: prompt.promptContext,
        }))
      );
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  // Fetch prompts only once on component mount
  useEffect(() => {
    if (prompts.length === 0) {
      fetchPrompts();
    }
  }, [prompts.length, fetchPrompts]);

  // Check agent status on component mount and when mode changes to agent
  useEffect(() => {
    if (selectedMode === "bedrock-agent") {
      // Add a small delay to prevent immediate queue flooding
      const timeoutId = setTimeout(() => {
        checkAgentStatus();
      }, 1000); // 1 second delay

      return () => clearTimeout(timeoutId);
    }
  }, [checkAgentStatus, selectedMode]);

  // Update queue status periodically
  useEffect(() => {
    const updateQueueStatus = () => {
      const status = requestQueueManager.getQueueStatus();
      setQueueStatus(status);
    };

    updateQueueStatus(); // Initial update
    const interval = setInterval(updateQueueStatus, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  // Session persistence
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem("chatSessionId", sessionId);
    }
    if (userId) {
      localStorage.setItem("chatUserId", userId);
    }
  }, [sessionId, userId]);

  // Mode persistence
  useEffect(() => {
    localStorage.setItem("chatSelectedMode", selectedMode);
  }, [selectedMode]);

  // Monitor WebSocket connection status for agent mode
  useEffect(() => {
    if (selectedMode === "bedrock-agent") {
      // Set up connection status monitoring
      const connectionHandler = (event) => {
        if (event.type === "connected") {
          setWsConnectionStatus({
            isConnected: true,
            isConnecting: false,
            connectionId: event.connectionId,
          });
        } else if (event.type === "disconnected") {
          setWsConnectionStatus({
            isConnected: false,
            isConnecting: false,
            connectionId: null,
          });
        }
      };

      const unsubscribe = webSocketService.onConnection(connectionHandler);

      // Update initial status
      const status = webSocketService.getStatus();
      setWsConnectionStatus({
        isConnected: status.isConnected,
        isConnecting: status.isConnecting,
        connectionId: status.connectionId,
      });

      // Pre-connect for agent mode
      if (!status.isConnected && !status.isConnecting) {
        setWsConnectionStatus((prev) => ({ ...prev, isConnecting: true }));
        webSocketService.connect().catch((error) => {
          console.error("Failed to connect WebSocket:", error);
          setWsConnectionStatus({
            isConnected: false,
            isConnecting: false,
            connectionId: null,
          });
        });
      }

      return () => {
        unsubscribe();
      };
    } else {
      // Reset connection status for non-agent modes
      setWsConnectionStatus({
        isConnected: false,
        isConnecting: false,
        connectionId: null,
      });
    }
  }, [selectedMode]);

  // Restore conversation on component mount
  useEffect(() => {
    const restoreConversation = async () => {
      const savedSessionId = localStorage.getItem("chatSessionId");
      // Only restore if we have a session ID
      if (savedSessionId) {
        try {
          console.log("Restoring conversation for session:", savedSessionId);
          const data = await chatAiApi.getConversation(savedSessionId);

          // Sort messages by timestamp to ensure proper chronological order
          const sortedMessages = data.messages.sort((a, b) => {
            const timeA = new Date(a.querySubmittedAt).getTime();
            const timeB = new Date(b.querySubmittedAt).getTime();
            return timeA - timeB; // Ascending order (oldest first)
          });

          const formattedMessages = sortedMessages
            .map((msg, index) => [
              {
                id: `${msg.messageId}-user`,
                role: "user",
                content: msg.userQuery,
                timestamp: new Date(msg.querySubmittedAt).toLocaleTimeString(
                  [],
                  { hour: "2-digit", minute: "2-digit" }
                ),
                sortTime: new Date(msg.querySubmittedAt).getTime(), // For debugging
              },
              {
                id: msg.messageId,
                role: "assistant",
                content: msg.generatedResponse,
                citations: msg.citations || [],
                feedback: msg.feedback?.rating ? msg.feedback : null,
                timestamp: new Date(msg.responseGeneratedAt).toLocaleTimeString(
                  [],
                  { hour: "2-digit", minute: "2-digit" }
                ),
                sortTime: new Date(msg.responseGeneratedAt).getTime(), // For debugging
              },
            ])
            .flat();

          // Ensure messages are in chronological order
          const chronologicalMessages = formattedMessages.sort((a, b) => {
            return (a.sortTime || 0) - (b.sortTime || 0);
          });

          setMessages(chronologicalMessages);
          setConversationTurn(sortedMessages.length + 1);
          console.log(
            `Restored conversation with ${sortedMessages.length} messages in chronological order`
          );
          console.log(
            "Message order check:",
            formattedMessages.map((msg) => ({
              role: msg.role,
              timestamp: msg.timestamp,
              sortTime: msg.sortTime,
              content: msg.content.substring(0, 50) + "...",
            }))
          );
        } catch (error) {
          console.error("Error restoring conversation:", error);
        }
      }
    };

    // Only restore conversation once on mount if no messages exist
    if (messages.length === 0) {
      restoreConversation();
    }
  }, [messages.length]); // Include messages.length in dependencies

  const handlePromptChange = ({ detail }) => {
    setSelectedPrompt(detail.selectedOption);
    if (detail.selectedOption) {
      setInput(detail.selectedOption.context);
    }
  };

  const handleGenAIOptionsChange = useCallback((options) => {
    setGenAIOptions(options);
  }, []);

  const handleSourceChange = (sources) => {
    console.log(
      "📊 Data Sources Selected:",
      sources.length > 0 ? sources.map((s) => s.name || s.dataSourceId) : "None"
    );
    console.log("📊 Data Source Filter Visibility:", showDataSourceFilter);
    setSelectedSources(sources);
  };

  const startNewConversation = () => {
    setMessages([]);
    setSessionId(null);
    setUserId(null);

    // Reset WebSocket session for new conversation
    if (selectedMode === "bedrock-agent") {
      webSocketService.resetSession();
    }
    setConversationTurn(1);

    // Clear agent-specific state but preserve mode selection

    localStorage.removeItem("chatSessionId");
    localStorage.removeItem("chatUserId");
    console.log("Started new conversation, mode preserved:", selectedMode);
  };

  // Process agent trace data into UI-friendly format
  const processAgentTrace = (traceData) => {
    if (!traceData || !Array.isArray(traceData)) {
      return [];
    }

    try {
      return traceData.map((trace, index) => {
        // Map actual Bedrock Agent trace events to user-friendly descriptions
        const getAgentStepTitle = (trace) => {
          if (trace.actionType === "KNOWLEDGE_BASE_LOOKUP") {
            return "🔍 Knowledge Base Search";
          }
          if (trace.actionType === "INVOKE_MODEL") {
            return "🤖 Model Invocation";
          }
          if (trace.actionType === "ORCHESTRATION") {
            return "🎯 Response Orchestration";
          }
          if (trace.actionType === "PREPROCESSING") {
            return "⚙️ Query Preprocessing";
          }
          if (trace.actionType === "POSTPROCESSING") {
            return "✨ Response Refinement";
          }
          if (trace.stepName) {
            return `🧠 ${trace.stepName}`;
          }
          return `🔄 Agent Step ${index + 1}`;
        };

        const getAgentStepDescription = (trace) => {
          if (trace.actionType === "KNOWLEDGE_BASE_LOOKUP") {
            const kbNames =
              trace.knowledgeBases?.map((kb) => kb.name || kb.id).join(", ") ||
              "knowledge bases";
            return `Searching ${kbNames} for relevant information to answer your query`;
          }
          if (trace.actionType === "INVOKE_MODEL") {
            return `Generating response using foundation model with retrieved context`;
          }
          if (trace.actionType === "ORCHESTRATION") {
            return `Coordinating multiple information sources and planning response structure`;
          }
          if (trace.actionType === "PREPROCESSING") {
            return `Analyzing query intent and preparing search parameters`;
          }
          if (trace.actionType === "POSTPROCESSING") {
            return `Refining response quality and ensuring accuracy`;
          }
          if (trace.observation) {
            return trace.observation;
          }
          if (trace.rationale) {
            return trace.rationale;
          }
          if (trace.description) {
            return trace.description;
          }
          return "Processing agent workflow step";
        };

        const step = {
          id: trace.traceId || `agent-step-${index}`,
          title: getAgentStepTitle(trace),
          description: getAgentStepDescription(trace),
          status: trace.status || "completed",
          stepNumber: index + 1,
          timestamp: trace.timestamp || new Date().toISOString(),
          details: {},
        };

        // Extract knowledge base information with better formatting
        if (trace.knowledgeBases && Array.isArray(trace.knowledgeBases)) {
          step.details.knowledgeBases = trace.knowledgeBases.map(
            (kb) => kb.name || kb.id
          );
        }

        // Extract action information with user-friendly labels
        if (trace.actionType || trace.action) {
          const actionType = trace.actionType || trace.action;
          step.details.action = actionType.replace(/_/g, " ").toLowerCase();
        }

        // Extract confidence score
        if (trace.confidence !== undefined) {
          step.details.confidence = parseFloat(trace.confidence);
        }

        // Extract source information
        if (trace.sources && Array.isArray(trace.sources)) {
          step.details.sources = trace.sources.map((source) => ({
            name: source.name || source.title,
            uri: source.uri || source.url,
            type: source.type || "document",
          }));
        }

        // Add retrieval results count if available
        if (trace.retrievalResults && Array.isArray(trace.retrievalResults)) {
          step.details.retrievalCount = trace.retrievalResults.length;
        }

        // Add model information if available
        if (trace.modelId) {
          step.details.model = trace.modelId;
        }

        return step;
      });
    } catch (error) {
      console.error("Error processing agent trace data:", error);
      return [
        {
          id: "error-step",
          title: "❌ Agent Processing Error",
          description:
            "Unable to parse reasoning steps from agent response. The agent may have encountered an internal error.",
          status: "error",
          stepNumber: 1,
          details: { error: error.message },
        },
      ];
    }
  };

  // Generate Bedrock Agent-style reasoning steps
  const generateAgentReasoning = (query, mode) => {
    if (mode === "manual") {
      // Manual mode uses direct API reasoning
      return [
        "Parsing user query and extracting intent",
        "Selecting configured data sources",
        "Executing direct API calls",
        "Processing raw response data",
        "Formatting results for presentation",
      ];
    }

    // Bedrock Agent mode - simulate actual agent thinking process
    const reasoningSteps = [];
    const queryLower = query.toLowerCase();

    // Step 1: Intent Recognition and Planning
    reasoningSteps.push("🧠 Analyzing user intent and planning approach");

    // Step 2: Knowledge Base Selection
    if (
      queryLower.includes("cost") ||
      queryLower.includes("billing") ||
      queryLower.includes("price")
    ) {
      reasoningSteps.push("💰 Routing to cost optimization knowledge base");
    } else if (
      queryLower.includes("security") ||
      queryLower.includes("compliance") ||
      queryLower.includes("access")
    ) {
      reasoningSteps.push(
        "🔒 Accessing security and compliance knowledge base"
      );
    } else if (
      queryLower.includes("performance") ||
      queryLower.includes("latency") ||
      queryLower.includes("speed")
    ) {
      reasoningSteps.push(
        "⚡ Consulting performance optimization knowledge base"
      );
    } else if (
      queryLower.includes("architecture") ||
      queryLower.includes("design") ||
      queryLower.includes("pattern")
    ) {
      reasoningSteps.push(
        "🏗️ Engaging architecture best practices knowledge base"
      );
    } else {
      reasoningSteps.push(
        "📚 Selecting relevant knowledge bases based on query context"
      );
    }

    // Step 3: Information Retrieval
    if (
      queryLower.includes("compare") ||
      queryLower.includes("vs") ||
      queryLower.includes("difference")
    ) {
      reasoningSteps.push(
        "🔍 Retrieving comparative data from multiple sources"
      );
    } else if (
      queryLower.includes("recommend") ||
      queryLower.includes("suggest") ||
      queryLower.includes("best")
    ) {
      reasoningSteps.push("🎯 Gathering recommendation criteria and options");
    } else if (
      queryLower.includes("how") ||
      queryLower.includes("step") ||
      queryLower.includes("guide")
    ) {
      reasoningSteps.push("📋 Collecting procedural and instructional content");
    } else {
      reasoningSteps.push("🔎 Searching and retrieving relevant information");
    }

    // Step 4: Context Analysis
    reasoningSteps.push(
      "🧩 Analyzing retrieved information and building context"
    );

    // Step 5: Response Generation Strategy
    if (
      queryLower.includes("sql") ||
      queryLower.includes("query") ||
      queryLower.includes("database")
    ) {
      reasoningSteps.push("💾 Formulating data-driven response with examples");
    } else if (
      queryLower.includes("code") ||
      queryLower.includes("script") ||
      queryLower.includes("implement")
    ) {
      reasoningSteps.push("💻 Preparing technical implementation guidance");
    } else if (
      queryLower.includes("explain") ||
      queryLower.includes("what is") ||
      queryLower.includes("define")
    ) {
      reasoningSteps.push("📖 Structuring educational and explanatory content");
    } else {
      reasoningSteps.push(
        "✨ Synthesizing comprehensive response with actionable insights"
      );
    }

    // Step 6: Quality Assurance
    reasoningSteps.push("✅ Validating response accuracy and completeness");

    return reasoningSteps;
  };

  // Animate reasoning steps
  const animateReasoning = async (steps, messageId) => {
    const stepDelay = Math.max(800, Math.min(2000, 8000 / steps.length)); // Adaptive timing

    for (let i = 0; i < steps.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, stepDelay));

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === messageId && msg.role === "assistant") {
            const newReasoning = steps.slice(0, i + 1).map((step, index) => ({
              text: step,
              isActive: index === i,
            }));
            return { ...msg, reasoning: newReasoning };
          }
          return msg;
        })
      );
    }

    // Mark all steps as complete
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === messageId && msg.role === "assistant") {
          const completedReasoning = steps.map((step) => ({
            text: step,
            isActive: false,
          }));
          return { ...msg, reasoning: completedReasoning };
        }
        return msg;
      })
    );
  };

  const handleSubmit = async () => {
    if (!input.trim()) return;

    setIsLoading(true);
    const timestamp = Date.now();
    const messageId = `frontend_${timestamp}_${timestamp}`; // Format: sessionId_timestamp_timestamp for compatibility
    const userMessage = {
      id: messageId,
      role: "user",
      content: input,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Create initial assistant message with reasoning placeholder
    const assistantMessageId = `${messageId}-response`;
    const initialAssistantMessage = {
      id: assistantMessageId,
      role: "assistant",
      content: "",
      reasoning: [],
      citations: [],
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, initialAssistantMessage]);

    // Start reasoning animation with mode-specific steps
    const reasoningSteps = generateAgentReasoning(input, selectedMode);
    const reasoningPromise = animateReasoning(
      reasoningSteps,
      assistantMessageId
    );

    try {
      console.log("🚀 Sending Chat Request:", {
        model: genAIOptions?.model || "default",
        dataSources: selectedSources.length,
        question: input.substring(0, 50) + (input.length > 50 ? "..." : ""),
      });

      const data = await chatAiApi.sendMessage(
        {
          message: input,
          selectedSources: selectedSources,
          genAIOptions,
          messageId,
          sessionId: sessionId,
          userId: userId,
          mode: selectedMode, // Include mode parameter for backend routing
        },
        (progress, streamingContent) => {
          const status = requestQueueManager.getQueueStatus();
          const queueInfo =
            status.chatQueue.queued > 0
              ? ` (${status.chatQueue.queued} requests queued)`
              : "";
          setProgressMessage(progress + queueInfo);

          // Update streaming content in real-time if provided
          if (streamingContent !== undefined) {
            setMessages((prev) =>
              prev.map((msg) => {
                if (msg.id === assistantMessageId) {
                  return {
                    ...msg,
                    content: streamingContent,
                    isStreaming: true,
                  };
                }
                return msg;
              })
            );
          }
        }
      );
      console.log("✅ Response Received:", {
        citations: data.citations?.length || 0,
        tokens: data.usage?.total_tokens || "unknown",
      });

      if (data.error) {
        throw new Error(data.error);
      }

      // Wait for reasoning animation to complete
      await reasoningPromise;

      // Process reasoning steps based on mode and response data
      let processedReasoning = [];
      if (
        selectedMode === "bedrock-agent" &&
        data.agentTrace &&
        data.agentTrace.length > 0
      ) {
        // Use actual agent trace data for reasoning steps
        processedReasoning = processAgentTrace(data.agentTrace);
        console.log(
          "Using actual agent trace data:",
          processedReasoning.length,
          "steps"
        );
      } else {
        // Use mode-specific simulated reasoning steps
        processedReasoning = reasoningSteps.map((step, index) => ({
          id: `simulated-step-${index}`,
          title:
            step.includes("🧠") ||
            step.includes("💰") ||
            step.includes("🔒") ||
            step.includes("⚡") ||
            step.includes("🏗️") ||
            step.includes("📚") ||
            step.includes("🔍") ||
            step.includes("🎯") ||
            step.includes("📋") ||
            step.includes("🔎") ||
            step.includes("🧩") ||
            step.includes("💾") ||
            step.includes("💻") ||
            step.includes("📖") ||
            step.includes("✨") ||
            step.includes("✅")
              ? step
              : `Processing Step ${index + 1}`,
          description: step,
          text: step, // Keep for backward compatibility
          isActive: false,
          status: "completed",
          stepNumber: index + 1,
          details: {},
        }));
        console.log(
          `Using ${selectedMode} simulated reasoning:`,
          processedReasoning.length,
          "steps"
        );
      }

      // Update the assistant message with the actual response (keep same ID for React consistency)
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === assistantMessageId) {
            const updatedMessage = {
              ...msg,
              // Keep the same ID to prevent React key conflicts
              content: data.response,
              citations: data.citations || [],
              reasoning: processedReasoning,
              isStreaming: false, // Mark streaming as complete
              apiMessageId: data.messageId, // Store API message ID separately for feedback
            };
            console.log("📝 Updated message for feedback support:", {
              uiMessageId: assistantMessageId,
              apiMessageId: data.messageId,
              mode: selectedMode,
              hasApiMessageId: !!data.messageId,
              reasoningSteps: processedReasoning.length,
            });
            return updatedMessage;
          }
          return msg;
        })
      );

      // Update session information from backend response
      if (data.sessionId && !sessionId) {
        setSessionId(data.sessionId);
      }
      if (data.userId && !userId) {
        setUserId(data.userId);
      }
      if (data.conversationTurn) {
        setConversationTurn(data.conversationTurn + 1);
      }
    } catch (error) {
      console.error("Error:", error);

      let errorMessage =
        error.message || "An unexpected error occurred. Please try again.";

      // Handle specific timeout scenarios
      if (
        error.message?.includes("timed out") ||
        error.message?.includes("504") ||
        error.message?.includes("Endpoint request timed out")
      ) {
        if (selectedMode === "bedrock-agent") {
          errorMessage = `Bedrock Agent request timed out after processing your complex query. This is expected for comprehensive analysis requests. The agent processed your request but exceeded the API Gateway timeout limit. Try breaking your query into smaller, more specific parts, or use manual mode for complex analysis.`;
        } else {
          errorMessage = `Request timed out. This can happen with complex queries. The response might still be processing in the background. You can check your conversation history or try a simpler query.`;
        }
      }

      // Handle agent-specific errors
      if (
        selectedMode === "bedrock-agent" &&
        error.message?.includes("agent")
      ) {
        errorMessage = `Agent error: ${error.message}. You can try switching to manual mode if this persists.`;
        // 🚨 TEMPORARY: Disable auto-switching during testing - REMOVE AFTER TESTING
        console.log("🚨 Auto-switching to manual mode disabled for testing");
        /*
        // Optionally auto-switch to manual mode
        if (
          error.message.includes("unavailable") ||
          error.message.includes("not found")
        ) {
          setSelectedMode("manual");
          errorMessage += " Automatically switched to manual mode.";
        }
        */
      }

      // Update the assistant message with error
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === assistantMessageId) {
            return {
              ...msg,
              content: `❌ Error: ${errorMessage}`,
              reasoning: [],
              citations: [],
            };
          }
          return msg;
        })
      );
    } finally {
      setIsLoading(false);
      setProgressMessage("");
      setCurrentReasoning([]);
      setInput("");
      setSelectedPrompt(null);
    }
  };

  return (
    <div className="chat-interface-wrapper">
      <SpaceBetween size="l">
        <div className="chat-header">
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <CustomButton
                  onClick={startNewConversation}
                  iconName="add-plus"
                >
                  New Conversation
                </CustomButton>
                {/* Only show data source filter button in manual mode */}
                {selectedMode === "manual" && (
                  <CustomButton
                    onClick={() =>
                      setShowDataSourceFilter(!showDataSourceFilter)
                    }
                    iconName="filter"
                  >
                    {showDataSourceFilter
                      ? "Hide Data Sources"
                      : "Show Data Sources"}
                  </CustomButton>
                )}
                {/* Only show GenAI options button in manual mode */}
                {selectedMode === "manual" && (
                  <CustomButton
                    onClick={() => setShowGenAIOptions(!showGenAIOptions)}
                    iconName="settings"
                  >
                    {showGenAIOptions
                      ? "Hide GenAI Options"
                      : "Show GenAI Options"}
                  </CustomButton>
                )}
              </SpaceBetween>
            }
          >
            GenAI Cloud Intelligence Assistant{" "}
            <Badge color={selectedMode === "bedrock-agent" ? "green" : "blue"}>
              {selectedMode === "bedrock-agent" ? "Agent Mode" : "Manual Mode"}
            </Badge>
            {selectedMode === "bedrock-agent" && (
              <Box margin={{ left: "s" }} display="inline-block">
                <Badge
                  color={
                    wsConnectionStatus.isConnecting
                      ? "grey"
                      : wsConnectionStatus.isConnected
                      ? "green"
                      : "red"
                  }
                >
                  {wsConnectionStatus.isConnecting
                    ? "🔄 Connecting..."
                    : wsConnectionStatus.isConnected
                    ? "🟢 Streaming Ready"
                    : "🔴 Disconnected"}
                </Badge>
              </Box>
            )}
            {sessionId && (
              <span style={{ fontSize: "0.7em", color: "#666" }}>
                ({conversationTurn - 1} messages)
              </span>
            )}
            {(queueStatus.chatQueue.queued > 0 ||
              queueStatus.apiQueue.queued > 0) && (
              <Box margin={{ left: "s" }} display="inline-block">
                <Badge color="blue">
                  Queue:{" "}
                  {queueStatus.chatQueue.active + queueStatus.apiQueue.active}{" "}
                  active,{" "}
                  {queueStatus.chatQueue.queued + queueStatus.apiQueue.queued}{" "}
                  waiting
                </Badge>
              </Box>
            )}
          </Header>
        </div>

        {/* Compact Mode Selector */}
        <div className="compact-mode-selector">
          <ModeSelector
            selectedMode={selectedMode}
            onModeChange={handleModeChange}
            agentAvailable={agentStatus?.available !== false}
            agentStatus={agentStatus}
            onRefreshAgent={checkAgentStatus}
            wsConnectionStatus={wsConnectionStatus}
          />
        </div>

        <div
          className={`layout-grid ${
            selectedMode === "manual" &&
            showGenAIOptions &&
            showDataSourceFilter
              ? "layout-grid-three-columns"
              : selectedMode === "manual" &&
                (showGenAIOptions || showDataSourceFilter)
              ? "layout-grid-with-filter"
              : ""
          }`}
        >
          <Box className="chat-container">
            <Box className="messages-area">
              {messages.length === 0 ? (
                <Box className="empty-state">
                  <Icon name="chat" size="big" variant="subtle" />
                  <Box variant="h3" padding={{ top: "m" }}>
                    Welcome to GenAI Cloud Intelligence Assistant
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    {selectedMode === "bedrock-agent"
                      ? "Start a conversation by typing a message. The agent will automatically handle data sources and optimization."
                      : "Start a conversation by typing a message or selecting a prompt below. Use the configuration options to customize your experience."}
                  </Box>
                </Box>
              ) : (
                <div className="messages-container">
                  {messages.map((message, index) => (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      onFeedbackClick={handleFeedbackClick}
                    />
                  ))}
                  {isLoading && (
                    <div className="message-bubble">
                      <div className="message-container assistant">
                        <div className="message-avatar">
                          <Icon
                            name="settings"
                            size="medium"
                            variant="subtle"
                          />
                        </div>
                        <div className="message-content">
                          <div className="message-text">
                            <Box>
                              <Icon name="status-in-progress" />
                              {selectedMode === "bedrock-agent"
                                ? "Agent is processing your request..."
                                : "Processing your request..."}
                              <Box
                                variant="p"
                                color="text-body-secondary"
                                fontSize="body-s"
                                padding={{ top: "xs" }}
                              >
                                {progressMessage ||
                                  (selectedMode === "bedrock-agent"
                                    ? "The agent is analyzing your request and selecting the best approach. This may take a moment."
                                    : "Complex queries may take up to 5 minutes to process. Please wait.")}
                              </Box>
                            </Box>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </Box>

            <Box className="input-area">
              <SpaceBetween size="m">
                <FormField
                  label="Select from Prompt Library"
                  description="Choose a pre-defined prompt template or type your own message"
                >
                  <Select
                    selectedOption={selectedPrompt}
                    onChange={handlePromptChange}
                    options={prompts}
                    placeholder="Choose a prompt"
                    expandToViewport
                  />
                </FormField>

                <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
                  <Textarea
                    value={input}
                    onChange={({ detail }) => setInput(detail.value)}
                    placeholder={
                      selectedMode === "bedrock-agent"
                        ? "Ask me anything - I'll handle the details automatically..."
                        : "Type your message..."
                    }
                    rows={3}
                  />
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    loading={isLoading}
                    disabled={
                      !input.trim() ||
                      isLoading ||
                      (selectedMode === "bedrock-agent" &&
                        !agentStatus?.available)
                    }
                  >
                    Send
                  </Button>
                </Grid>
              </SpaceBetween>
            </Box>
          </Box>

          {/* Only show configuration panels in manual mode */}
          {selectedMode === "manual" && showDataSourceFilter && (
            <div className="filter-container">
              {dataSourceError && (
                <Box margin={{ bottom: "s" }} color="error">
                  <TextContent>
                    <p>{dataSourceError}</p>
                  </TextContent>
                </Box>
              )}
              <DataSourceFilter
                onFilterChange={handleSourceChange}
                onError={setDataSourceError}
                selectedSources={selectedSources}
              />
            </div>
          )}

          {selectedMode === "manual" && showGenAIOptions && (
            <div className="filter-container">
              <SpaceBetween size="l">
                <GenAIOptions onOptionsChange={handleGenAIOptionsChange} />
              </SpaceBetween>
            </div>
          )}
        </div>
      </SpaceBetween>

      <FeedbackModal
        visible={feedbackModalVisible}
        message={selectedMessage}
        onSubmit={handleFeedbackSubmit}
        onDismiss={() => {
          console.log("Dismissing feedback modal");
          setFeedbackModalVisible(false);
          setSelectedMessage(null);
        }}
      />
    </div>
  );
};

export default ChatInterface;
